import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import { PageHeader } from "../components/RequestRouting";
import { DEMO_EMPLOYEE } from "../lib/roles";

type Allowability = "allowable" | "unallowable" | "conditionally_unallowable";

type AccountRow = {
  code: string;
  name: string;
  allowability: Allowability;
};

type ChecklistItem = {
  id: string;
  criterion: string;
  status: "compliant" | "partial" | "gap";
  note: string;
};

const ACCOUNTS: AccountRow[] = [
  { code: "5100", name: "Direct labor", allowability: "allowable" },
  { code: "6100", name: "G&A labor", allowability: "allowable" },
  { code: "6900", name: "Entertainment (unallowable)", allowability: "unallowable" },
  { code: "6910", name: "Lobbying (unallowable)", allowability: "unallowable" },
  { code: "6250", name: "Business development (conditional)", allowability: "conditionally_unallowable" },
];

const INITIAL_CHECKLIST: ChecklistItem[] = [
  { id: "9", criterion: "(9) Timekeeping by cost objective", status: "partial", note: "REL-011..013 demosite" },
  { id: "10", criterion: "(10) Labor distribution", status: "partial", note: "REL-015 Labor page" },
  { id: "12", criterion: "(12) Exclude unallowables", status: "partial", note: "REL-016 allowability flags" },
  { id: "15", criterion: "(15) LOC/LOF + indirect rates", status: "partial", note: "Funding threshold demo" },
  { id: "16", criterion: "(16) Billings reconcile to cost", status: "partial", note: "Billing reconcile stub" },
];

export function DcaaCompliancePage() {
  const { moduleIds, homePath, roleIds } = useAuth();
  const canAudit =
    roleIds.includes("ROLE-EXECUTIVE-BI-AUDITOR") ||
    roleIds.includes("ROLE-FINANCE-ACCOUNTING") ||
    roleIds.includes("ROLE-COMPANY-ADMIN");

  const [policyAck, setPolicyAck] = useState(false);
  const [policyVersion] = useState("TK-POL-2026.08");
  const [fundingPct] = useState(78);
  const [checklist, setChecklist] = useState(INITIAL_CHECKLIST);
  const [flash, setFlash] = useState<string | null>(null);
  const [clinRequired] = useState(true);
  const [clinValue, setClinValue] = useState("");

  const billableBlocked = useMemo(
    () => ACCOUNTS.filter((a) => a.allowability === "unallowable"),
    [],
  );

  if (!canAudit && !moduleIds.includes("bi") && !moduleIds.includes("gl")) {
    return <Navigate to={homePath} replace />;
  }

  function exportFloorCheck() {
    const body = [
      "GovSuite floor-check pack (demo)",
      `As of: ${new Date().toISOString()}`,
      `Employee: ${DEMO_EMPLOYEE.name}`,
      "LCAT: Engineer II",
      "Supervisor: Jordan Ortiz",
      "Remote: hybrid",
      "Today charges: 01.014.000.0001.ENG 4.0; IND-GA-0001 1.0",
    ].join("\n");
    downloadText("floor-check-pack.txt", body);
    setFlash("Floor-check pack downloaded (FloorCheckPackExported stub).");
  }

  function exportEvidence() {
    const body = [
      "GovSuite DCAA labor evidence pack (demo)",
      "Includes: certifications, approvals, revisions, LDR stub, payroll reconcile stub, GL ties",
      `Policy version acknowledged: ${policyAck ? policyVersion : "not acknowledged"}`,
      "Retention: timesheet/labor_distribution FAR 4.7 theme",
    ].join("\n");
    downloadText("dcaa-labor-evidence-pack.txt", body);
    setFlash("Labor evidence pack downloaded (LaborEvidencePackExported stub).");
  }

  function downloadText(filename: string, body: string) {
    const blob = new Blob([body], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="DCAA compliance demo"
        subtitle="Unallowables, CLIN/funds, billing reconcile stubs, policy ack, and evidence packs (REL-016/017)."
      />

      {flash && (
        <div className="border border-teal/40 bg-teal/10 px-3 py-2 text-sm text-navy">{flash}</div>
      )}

      <section className="bg-white border border-black/10 p-4 space-y-2">
        <h2 className="font-semibold text-navy">Account allowability</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-mist text-left">
              <th className="px-2 py-2">Account</th>
              <th className="px-2 py-2">Name</th>
              <th className="px-2 py-2">Allowability</th>
              <th className="px-2 py-2">Gov billable</th>
            </tr>
          </thead>
          <tbody>
            {ACCOUNTS.map((a) => (
              <tr key={a.code} className="border-t border-black/5">
                <td className="px-2 py-1.5 font-mono">{a.code}</td>
                <td className="px-2 py-1.5">{a.name}</td>
                <td className="px-2 py-1.5">{a.allowability}</td>
                <td className="px-2 py-1.5">
                  {a.allowability === "unallowable" ? (
                    <span className="text-red-700 text-xs font-medium">Blocked</span>
                  ) : (
                    <span className="text-teal text-xs font-medium">Allowed</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-slate">
          Unallowable scrub list: {billableBlocked.map((a) => a.code).join(", ")} — excluded from
          provisional pools and government invoices.
        </p>
      </section>

      <section className="bg-white border border-black/10 p-4 space-y-2">
        <h2 className="font-semibold text-navy">CLIN / funding / billing</h2>
        {fundingPct >= 75 && (
          <div className="border border-gold bg-gold/10 px-3 py-2 text-sm text-navy">
            LOC/LOF alert: project burn at {fundingPct}% of allotted funds (threshold 75%/85%).
          </div>
        )}
        <label className="block text-sm text-navy space-y-1 max-w-sm">
          <span>
            CLIN on time{clinRequired ? " (required for demo contract)" : ""}
          </span>
          <input
            className="w-full border border-black/10 px-3 py-2 text-sm"
            value={clinValue}
            onChange={(e) => setClinValue(e.target.value)}
            placeholder="e.g. 0001AA"
          />
        </label>
        {clinRequired && !clinValue.trim() && (
          <p className="text-xs text-red-700">Submit would be blocked until CLIN is entered.</p>
        )}
        <div className="text-sm text-slate">
          Billing reconcile stub: Invoice INV-1001 current $42,000 / cumulative $180,000 ↔ cost
          ledger current $42,000 / cumulative $180,000 (matched). Backup package retained in
          Documents.
        </div>
        <Link className="text-sm text-teal underline" to="/app/labor">
          Open Labor distribution LDR
        </Link>
      </section>

      <section className="bg-white border border-black/10 p-4 space-y-3">
        <h2 className="font-semibold text-navy">Policy acknowledgment & packs</h2>
        <label className="flex gap-2 text-sm items-start">
          <input
            type="checkbox"
            className="mt-1"
            checked={policyAck}
            onChange={(e) => setPolicyAck(e.target.checked)}
          />
          <span>
            I acknowledge timekeeping policy <code>{policyVersion}</code> (nature of work drives
            charging; Total Time Accounting; false statements may violate 18 U.S.C. § 1001 —
            tenant-configurable).
          </span>
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="px-3 py-2 text-sm bg-navy text-white disabled:opacity-40"
            disabled={!policyAck}
            onClick={() => setFlash(`Policy ${policyVersion} acknowledged for ${DEMO_EMPLOYEE.name}.`)}
          >
            Save acknowledgment
          </button>
          <button type="button" className="px-3 py-2 text-sm border border-black/10" onClick={exportFloorCheck}>
            Download floor-check pack
          </button>
          <button type="button" className="px-3 py-2 text-sm border border-black/10" onClick={exportEvidence}>
            Download labor evidence pack
          </button>
        </div>
        <p className="text-xs text-slate">
          Remote/offline catch-up: use late-entry reason codes on Timekeeping (offline / secure
          facility / remote_access).
        </p>
      </section>

      <section className="bg-white border border-black/10 p-4 space-y-2">
        <h2 className="font-semibold text-navy">DFARS-18 self-checklist (subset)</h2>
        <ul className="space-y-2 text-sm">
          {checklist.map((c) => (
            <li key={c.id} className="border border-black/5 px-3 py-2 flex flex-wrap gap-2 items-center justify-between">
              <div>
                <div className="font-medium text-navy">{c.criterion}</div>
                <div className="text-xs text-slate">{c.note}</div>
              </div>
              <select
                className="border border-black/10 text-sm px-2 py-1"
                value={c.status}
                onChange={(e) =>
                  setChecklist((prev) =>
                    prev.map((x) =>
                      x.id === c.id
                        ? { ...x, status: e.target.value as ChecklistItem["status"] }
                        : x,
                    ),
                  )
                }
              >
                <option value="compliant">compliant</option>
                <option value="partial">partial</option>
                <option value="gap">gap</option>
              </select>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
